import type { AgCartesianChartOptions } from "ag-charts-community";
import { useMemo } from "react";

import { Chart } from "@web-app/components";

import ChartWrapper from "./ChartWrapper";
import { formatAxisValue } from "./formatAxisValue";

interface LineChartData {
  axisLabels?: {
    x?: string;
    y?: string;
  };
  datasets: Array<{
    data: number[];
    label: string;
  }>;
  labels: string[];
}

interface LineChartProps {
  data: LineChartData;
  title: string;
}

export default function LineChart({ data, title }: LineChartProps) {
  const chartData = useMemo(() => {
    if (!data?.labels || !data?.datasets?.length) return [];

    return data.labels.map((label, index) => {
      const dataPoint: Record<string, number | string> = { category: label };
      data.datasets.forEach((dataset, datasetIndex) => {
        if (datasetIndex === 0) {
          dataPoint.value = dataset.data[index] || 0;
        } else {
          dataPoint[`value${datasetIndex}`] = dataset.data[index] || 0;
        }
      });
      return dataPoint;
    });
  }, [data]);

  const chartOptions: AgCartesianChartOptions = useMemo(
    () => ({
      axes: [
        {
          position: "bottom",
          title: {
            enabled: true,
            text: data?.axisLabels?.x || "Category",
          },
          type: "category",
        },
        {
          label: {
            formatter: (params) => formatAxisValue(params.value),
          },
          position: "left",
          title: {
            enabled: true,
            text: data?.axisLabels?.y || data?.datasets?.[0]?.label || "Value",
          },
          type: "number",
        },
      ],
      data: chartData,
      series:
        data?.datasets?.map((dataset, index) => ({
          marker: {
            shape: "circle",
            size: 4,
            strokeWidth: 2,
          },
          strokeWidth: 2,
          type: "line",
          xKey: "category",
          yKey: index === 0 ? "value" : `value${index}`,
          yName: dataset.label || `Series ${index + 1}`,
        })) || [],
    }),
    [chartData, data],
  );

  return (
    <ChartWrapper title={title}>
      <Chart options={chartOptions} isLoading={false} />
    </ChartWrapper>
  );
}
